import _ from 'lodash';
import moment from 'moment';
import i18n from 'i18next';

const { DATE_FORMAT } = Mobi2Go.app;

const computeDateHelperStrings = (location, date, slots) => {
    date.date_value = date.format(DATE_FORMAT);
    date.surcharge = location.getSurchargeForDate(date);
    date.is_closed = slots.length === 0;
    switch (date.date_value) {
        case location.current_time.today:
            if (
                slots.length &&
                ((slots[0] instanceof moment && slots[0].hour() >= 17) ||
                    (!(slots[0] instanceof moment) && slots[0] >= 17 * 60))
            ) {
                // Considered tonight after 17h
                date.display_value = i18n.t('order.datetime:tonight');
            } else {
                date.display_value = i18n.t('order.datetime:today');
            }

            break;
        case location.current_time.tomorrow:
            date.display_value = i18n.t('order.datetime:tomorrow');
            break;
        default:
            date.display_value = date.format('ddd, Do MMM');
    }
};

const computeDateTimeHelperStrings = datetime => {
    datetime.datetime_value = datetime.toISOString(true);
    datetime.date_value = datetime.format(DATE_FORMAT);
    datetime.display_value = Mobi2Go.helpers.formatDatetimeToTimeDisplayValue(
        datetime
    );
};

const parseTimeSlots = (times, location) => {
    const parsed = {
        dates: {},
        times: {},
    };
    Object.keys(times)
        .sort()
        .forEach(date_string => {
            const date = moment(date_string)
                .startOf('day')
                .utcOffset(location.current_time.utcOffset(), true);
            const slots = times[date_string];

            computeDateHelperStrings(location, date, slots);

            parsed.dates[date_string] = date;
            parsed.times[date_string] = slots.map(slot => {
                const time_slot = date.clone().add(slot, 'minutes');
                computeDateTimeHelperStrings(time_slot);
                return time_slot;
            });
        });
    return parsed;
};

const timeSlotsComparator = (a, b) => {
    if (a instanceof moment && b instanceof moment) {
        return a.isSame(b);
    }
    return undefined;
};

class OrderTimeSlots {
    is_dirty = false;

    is_syncing = false;

    last_sync = null;

    time_slots = {};

    static SYNC_VALIDITY_DURATION_SECONDS = 5 * 60; // 5 minutes

    constructor(order_event_emitter) {
        this.bind = order_event_emitter.bind;
        this.unbind = order_event_emitter.unbind;
        this.once = order_event_emitter.once;
        this.trigger = order_event_emitter.trigger;
    }

    get() {
        const location = Mobi2Go.Order.getLocation();
        const method = Mobi2Go.Order.getMethod();
        if (!location || !method) {
            return null;
        }
        const access_key = `${location.id}_${method}`;
        if (!(access_key in this.time_slots)) {
            return null;
        }
        return this.time_slots[access_key];
    }

    getDates() {
        const time_slots = this.get();
        if (!time_slots) {
            return [];
        }

        return Object.values(time_slots.dates);
    }

    getTimes(date) {
        const time_slots = this.get();
        if (!time_slots) {
            return [];
        }
        const location = Mobi2Go.Order.getLocation();

        let for_date = date;
        if (!for_date) {
            // if no date provided give times for first available day
            [for_date] = Object.values(time_slots.dates);
        }
        if (!(for_date instanceof moment)) {
            for_date = moment(for_date);
            for_date.utcOffset(location.current_time.utcOffset(), true);
        }
        const for_date_string = for_date.format(DATE_FORMAT);
        if (!(for_date_string in time_slots.times)) {
            return [];
        }

        return time_slots.times[for_date_string];
    }

    getFirstTimeSlot() {
        const dates = this.getDates();
        if (!dates.length) {
            return null;
        }
        const times = this.getTimes(dates[0]);
        if (!times.length) {
            return null;
        }
        return times[0];
    }

    getLastTimeSlotOfFirstDay() {
        const dates = this.getDates();
        if (!dates.length) {
            return null;
        }
        const times = this.getTimes(dates[0]);
        if (!times.length) {
            return null;
        }
        return times[times.length - 1];
    }

    isDirty() {
        return this.is_dirty;
    }

    invalidate() {
        this.is_dirty = true;
    }

    secondsSinceLastSync() {
        if (this.last_sync === null) {
            return null;
        }
        return moment().diff(this.last_sync, 'seconds');
    }

    shouldSync() {
        return (
            this.secondsSinceLastSync() >=
                OrderTimeSlots.SYNC_VALIDITY_DURATION_SECONDS || this.isDirty()
        );
    }

    refresh() {
        const time_slots = this.get();
        if (!time_slots) {
            return;
        }
        const location = Mobi2Go.Order.getLocation();
        const first_time_slot = this.getFirstTimeSlot();
        const wait_time = Mobi2Go.Order.getWaitTime();
        const cutoff = location.current_time.clone().add(wait_time, 'minute');
        if (first_time_slot && cutoff.isBefore(first_time_slot)) {
            // no need to compute anything if we are still before the first available slot.
            return;
        }

        const time_slots_outdated = _.some(
            Object.keys(time_slots.dates),
            date_string => {
                const date = time_slots.dates[date_string];
                if (date.isBefore(cutoff, 'day')) {
                    return true;
                }
                return _.some(time_slots.times[date_string], time =>
                    time.isBefore(cutoff, 'minute')
                );
            }
        );

        if (time_slots_outdated) {
            this.invalidate();
        }
    }

    isSyncing() {
        return this.is_syncing;
    }

    setIsSyncing(is_syncing) {
        if (this.is_syncing === !!is_syncing) {
            return;
        }
        this.is_syncing = !!is_syncing;
        this.trigger('updateTimeSlotsIsSyncing', this.is_syncing);
    }

    internalSet(access_key, time_slots) {
        if (
            _.isEqualWith(
                this.time_slots[access_key],
                time_slots,
                timeSlotsComparator
            )
        ) {
            return;
        }
        this.time_slots[access_key] = time_slots;
        this.trigger('updateTimeSlots', time_slots);
    }

    set(data) {
        const location = Mobi2Go.Order.getLocation();
        const method = Mobi2Go.Order.getMethod();
        if (!data?.times || !location || !method) {
            return;
        }
        const access_key = `${location.id}_${method}`;
        const timeslots = parseTimeSlots(data.times, location);
        this.internalSet(access_key, timeslots);
        this.last_sync = moment();
        this.is_dirty = false;
        this.setIsSyncing(false);
    }
}

Mobi2Go._class = Mobi2Go._class || {};
Mobi2Go._class.OrderTimeSlots = OrderTimeSlots;

export default OrderTimeSlots;
